/**
 * Localized Date Utilities
 * 
 * These functions provide timezone and locale-aware date formatting.
 * They should be used with the useLocale() hook or LocaleContext.
 */

import { format, parseISO, isToday, isYesterday, differenceInMinutes, differenceInHours, differenceInDays, Locale } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { ptBR, enUS, es } from 'date-fns/locale';
import type { SupportedLanguage } from '@/locales';

// Locale mapping
const LOCALES: Record<SupportedLanguage, Locale> = {
  'pt-BR': ptBR,
  'en-US': enUS,
  'es': es,
};

// Date format patterns by language
const DATE_PATTERNS: Record<SupportedLanguage, {
  short: string;
  long: string;
  dateTime: string;
  dateTimeShort: string;
  time: string;
  timeWithSeconds: string;
}> = {
  'pt-BR': {
    short: 'dd/MM/yyyy',
    long: `d 'de' MMMM 'de' yyyy`,
    dateTime: `d 'de' MMMM 'de' yyyy 'às' HH:mm`,
    dateTimeShort: `dd/MM/yyyy 'às' HH:mm`,
    time: 'HH:mm',
    timeWithSeconds: 'HH:mm:ss',
  },
  'en-US': {
    short: 'MM/dd/yyyy',
    long: 'MMMM d, yyyy',
    dateTime: `MMMM d, yyyy 'at' h:mm a`,
    dateTimeShort: `MM/dd/yyyy 'at' h:mm a`,
    time: 'h:mm a',
    timeWithSeconds: 'h:mm:ss a',
  },
  'es': {
    short: 'dd/MM/yyyy',
    long: `d 'de' MMMM 'de' yyyy`,
    dateTime: `d 'de' MMMM 'de' yyyy 'a las' HH:mm`,
    dateTimeShort: `dd/MM/yyyy 'a las' HH:mm`,
    time: 'HH:mm',
    timeWithSeconds: 'HH:mm:ss',
  },
};

// Relative time labels
const RELATIVE_LABELS: Record<SupportedLanguage, {
  today: string;
  yesterday: string;
  tomorrow: string;
  now: string;
  minutesAgo: (n: number) => string;
  hoursAgo: (n: number) => string;
  daysAgo: (n: number) => string;
}> = {
  'pt-BR': {
    today: 'Hoje',
    yesterday: 'Ontem',
    tomorrow: 'Amanhã',
    now: 'Agora',
    minutesAgo: (n) => `${n} min atrás`,
    hoursAgo: (n) => `${n}h atrás`,
    daysAgo: (n) => `${n} dias atrás`,
  },
  'en-US': {
    today: 'Today',
    yesterday: 'Yesterday',
    tomorrow: 'Tomorrow',
    now: 'Now',
    minutesAgo: (n) => `${n} min ago`,
    hoursAgo: (n) => `${n}h ago`,
    daysAgo: (n) => `${n} days ago`,
  },
  'es': {
    today: 'Hoy',
    yesterday: 'Ayer',
    tomorrow: 'Mañana',
    now: 'Ahora',
    minutesAgo: (n) => `hace ${n} min`,
    hoursAgo: (n) => `hace ${n}h`,
    daysAgo: (n) => `hace ${n} días`,
  },
};

export interface FormatOptions {
  language?: SupportedLanguage;
  timezone?: string;
}

/**
 * Parse a date string or Date object safely
 */
export function parseDate(date: Date | string | number | null | undefined): Date | null {
  if (!date) return null;
  
  try {
    if (date instanceof Date) {
      return isNaN(date.getTime()) ? null : date;
    }
    
    if (typeof date === 'number') {
      return new Date(date);
    }
    
    // Handle ISO strings
    if (typeof date === 'string') {
      // If it's a date-only string (YYYY-MM-DD), parse as local date
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        const [year, month, day] = date.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      
      // Otherwise parse as ISO
      return parseISO(date);
    }
    
    return null;
  } catch {
    return null;
  }
}

/**
 * Format a date in a specific timezone with locale-aware patterns
 */
export function formatDateTime(
  date: Date | string | number | null | undefined,
  options: FormatOptions = {}
): string {
  const { language = 'pt-BR', timezone } = options;
  const parsedDate = parseDate(date);
  if (!parsedDate) return '';

  const pattern = DATE_PATTERNS[language].dateTime;
  const locale = LOCALES[language];

  if (timezone) {
    return formatInTimeZone(parsedDate, timezone, pattern, { locale });
  }
  
  return format(parsedDate, pattern, { locale });
}

/**
 * Format a date with short pattern
 */
export function formatDateShort(
  date: Date | string | number | null | undefined,
  options: FormatOptions = {}
): string {
  const { language = 'pt-BR', timezone } = options;
  const parsedDate = parseDate(date);
  if (!parsedDate) return '';

  const pattern = DATE_PATTERNS[language].short;
  const locale = LOCALES[language];

  if (timezone) {
    return formatInTimeZone(parsedDate, timezone, pattern, { locale });
  }
  
  return format(parsedDate, pattern, { locale });
}

/**
 * Format a date with long pattern
 */
export function formatDateLong(
  date: Date | string | number | null | undefined,
  options: FormatOptions = {}
): string {
  const { language = 'pt-BR', timezone } = options;
  const parsedDate = parseDate(date);
  if (!parsedDate) return '';

  const pattern = DATE_PATTERNS[language].long;
  const locale = LOCALES[language];

  if (timezone) {
    return formatInTimeZone(parsedDate, timezone, pattern, { locale });
  }
  
  return format(parsedDate, pattern, { locale });
}

/**
 * Format time only
 */
export function formatTime(
  date: Date | string | number | null | undefined,
  options: FormatOptions = {}
): string {
  const { language = 'pt-BR', timezone } = options;
  const parsedDate = parseDate(date);
  if (!parsedDate) return '';

  const pattern = DATE_PATTERNS[language].time;
  const locale = LOCALES[language];

  if (timezone) {
    return formatInTimeZone(parsedDate, timezone, pattern, { locale });
  }
  
  return format(parsedDate, pattern, { locale });
}

/**
 * Format as short date + time
 */
export function formatDateTimeShort(
  date: Date | string | number | null | undefined,
  options: FormatOptions = {}
): string {
  const { language = 'pt-BR', timezone } = options;
  const parsedDate = parseDate(date);
  if (!parsedDate) return '';

  const pattern = DATE_PATTERNS[language].dateTimeShort;
  const locale = LOCALES[language];

  if (timezone) {
    return formatInTimeZone(parsedDate, timezone, pattern, { locale });
  }
  
  return format(parsedDate, pattern, { locale });
}

/**
 * Format relative time (e.g., "5 min ago", "Yesterday")
 */
export function formatRelativeTime(
  date: Date | string | number | null | undefined,
  options: FormatOptions = {}
): string {
  const { language = 'pt-BR', timezone } = options;
  const parsedDate = parseDate(date);
  if (!parsedDate) return '';

  const labels = RELATIVE_LABELS[language];
  const now = new Date();
  
  // Convert to timezone if specified
  const compareDate = timezone ? toZonedTime(parsedDate, timezone) : parsedDate;
  const nowInTz = timezone ? toZonedTime(now, timezone) : now;

  // Check for today/yesterday
  if (isToday(compareDate)) {
    const minutes = differenceInMinutes(nowInTz, compareDate);
    if (minutes < 1) return labels.now;
    if (minutes < 60) return labels.minutesAgo(minutes);
    
    const hours = differenceInHours(nowInTz, compareDate);
    if (hours < 24) return labels.hoursAgo(hours);
  }

  if (isYesterday(compareDate)) {
    return labels.yesterday;
  }

  const days = differenceInDays(nowInTz, compareDate);
  if (days <= 7) {
    return labels.daysAgo(days);
  }

  // Fall back to short date format
  return formatDateShort(parsedDate, options);
}

/**
 * Format for WhatsApp-style timestamps
 */
export function formatWhatsAppTimestamp(
  date: Date | string | number | null | undefined,
  options: FormatOptions = {}
): { dateLabel: string; time: string } {
  const { language = 'pt-BR', timezone } = options;
  const parsedDate = parseDate(date);
  if (!parsedDate) return { dateLabel: '', time: '' };

  const labels = RELATIVE_LABELS[language];
  const compareDate = timezone ? toZonedTime(parsedDate, timezone) : parsedDate;

  let dateLabel: string;
  if (isToday(compareDate)) {
    dateLabel = labels.today;
  } else if (isYesterday(compareDate)) {
    dateLabel = labels.yesterday;
  } else {
    dateLabel = formatDateShort(parsedDate, options);
  }

  const time = formatTime(parsedDate, options);

  return { dateLabel, time };
}

/**
 * Format call duration in seconds to human readable format
 */
export function formatCallDuration(seconds: number | null | undefined, language: SupportedLanguage = 'pt-BR'): string {
  if (!seconds || seconds <= 0) return '0s';
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (minutes === 0) {
    return `${remainingSeconds}s`;
  }
  
  if (remainingSeconds === 0) {
    return `${minutes}m`;
  }
  
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Format date for input fields (always YYYY-MM-DD)
 */
export function formatDateForInput(date: Date | string | number | null | undefined): string {
  const parsedDate = parseDate(date);
  if (!parsedDate) return '';
  return format(parsedDate, 'yyyy-MM-dd');
}

/**
 * Get locale object for date-fns
 */
export function getDateFnsLocale(language: SupportedLanguage = 'pt-BR'): Locale {
  return LOCALES[language];
}

/**
 * Get date format patterns for a language
 */
export function getDatePatterns(language: SupportedLanguage = 'pt-BR') {
  return DATE_PATTERNS[language];
}
